// Ecotile module — shared front-end helpers (Phase 2).
// INR/lakh formatting (Indian digit grouping), date formatting, and the
// palette/metadata the register, payments board and targets view all share.
// Skin: brand YELLOW #FFCC00 + DARK GREY #3F4444 (the FY tracking sheet's own
// idiom). Chart series colours are the dataviz-validated pairs (light/dark).

export const ECO = {
  yellow: "#FFCC00",
  yellowDeep: "#E6B800",
  yellowInk: "#6e5600",
  grey: "#3F4444",
  green: "#188653",   // Paid
  orange: "#b35c00",  // Pending
  red: "#c22f2f",     // overdue
  amber: "#8a6a00",   // no-deadline
};

// order-type dot colours (kept distinct from the money/status palette)
export const TYPE_COLORS = {
  Project: "#5B7FA6",
  Supplementary: "#8A6FB8",
  Demo: "#22A377",
  Install: "#B07C4F",
  Freight: "#708090",
};

// payment buckets — the register indicator + the payments board share this.
export const BUCKET_META = {
  paid:     { label: "Paid",         tone: "green"  },
  overdue:  { label: "Overdue",      tone: "red"    },
  nodl:     { label: "No deadline",  tone: "amber"  },
  upcoming: { label: "Upcoming",     tone: "slate"  },
};

// ── money ──────────────────────────────────────────────────────────────────
// Indian digit grouping: 13659214.41 -> "1,36,59,214.41". Hides paise when .00.
export function inrDigits(n) {
  if (n === null || n === undefined || n === "") return "—";
  const neg = n < 0;
  const abs = Math.abs(Number(n));
  const [wholeRaw, paise] = abs.toFixed(2).split(".");
  let whole = wholeRaw;
  if (whole.length > 3) {
    const head = whole.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ",");
    whole = head + "," + whole.slice(-3);
  }
  return (neg ? "−" : "") + whole + (paise !== "00" ? "." + paise : "");
}
export const inr = (n) => (n === null || n === undefined ? "—" : "₹" + inrDigits(n));

// compact lakh / crore for stat context and charts
export function inrLakh(n) {
  if (n === null || n === undefined) return "—";
  return "₹" + (Number(n) / 100000).toFixed(2) + " L";
}
export function inrCr(n) {
  if (n === null || n === undefined) return "—";
  const cr = Number(n) / 10000000;
  return "₹" + cr.toFixed(cr >= 1 ? 2 : 2) + " Cr";
}
// auto: >= 1 crore -> Cr, else Lakh (for headline numbers)
export function inrShort(n) {
  if (n === null || n === undefined) return "—";
  return Math.abs(Number(n)) >= 10000000 ? inrCr(n) : inrLakh(n);
}

// ── dates ────────────────────────────────────────────────────────────────
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
export function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  if (isNaN(d)) return "";
  return d.getDate() + " " + MON[d.getMonth()] + " " + String(d.getFullYear()).slice(2);
}

// A one-line payment hint for the register indicator column.
// paid -> "Paid" · overdue -> "overdue 21d" · nodl -> "no deadline" ·
// upcoming -> "due 31 Jul". Returns {text, tone}.
export function paymentHint(o) {
  const bucket = o.payment_bucket;
  if (bucket === "paid") return { text: "Paid", tone: "green" };
  if (o.value_hidden) {
    if (bucket === "overdue") return { text: "overdue " + (o.days_overdue ?? "") + "d", tone: "red" };
    if (bucket === "nodl") return { text: "no deadline", tone: "amber" };
    if (bucket === "upcoming") return { text: "due " + fmtDate(o.deadline), tone: "slate" };
    return { text: "pending", tone: "slate" };
  }
  const pend = inr(o.amount_pending);
  if (bucket === "overdue") return { text: pend + " · overdue " + o.days_overdue + "d", tone: "red" };
  if (bucket === "nodl") return { text: pend + " · no deadline", tone: "amber" };
  if (bucket === "upcoming") return { text: pend + " · due " + fmtDate(o.deadline), tone: "slate" };
  return { text: pend + " pending", tone: "slate" };
}

// received / total ratio for the progress bar (0..1). 1 when paid.
export function receivedRatio(o) {
  if (o.payment_bucket === "paid") return 1;
  const total = Number(o.total_value_incl || 0);
  const recd = Number(o.received_so_far || 0);
  if (!total) return 0;
  return Math.max(0, Math.min(1, recd / total));
}

export const FY_DEFAULT = "2026-27";
